import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import WarningIcon from "components/svg/icons/WarningIcon";
import BasicButton from "components/forms/BasicButton";
import { getAdminAlertSummary, getClientAlertSummary, getProviderAlertSummary, AlertSummary, AdminAlertSummary } from "api/Dashboard";
import { getExpiringItems } from "api/QuoteExpiration";
import { themevals } from "theme/themevals";
import ActionItemsModal from "./ActionItemsModal";
import { claimLoginModal } from "helpers/loginModal";

interface ActionRequiredBannerProps {
  dashboardType?: "admin" | "manager" | "client" | "provider";
  onViewAll?: () => void;
}

const ActionRequiredBanner: React.FC<ActionRequiredBannerProps> = ({ dashboardType = "admin", onViewAll }) => {
  const [alertData, setAlertData] = useState<AlertSummary | AdminAlertSummary | null>(null);
  const [expiringCount, setExpiringCount] = useState(0);
  const [expiringCriticalCount, setExpiringCriticalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAdmin = dashboardType === "admin" || dashboardType === "manager";

  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);
      let data: AlertSummary | AdminAlertSummary | null = null;

      if (dashboardType === "client") {
        data = await getClientAlertSummary();
      } else if (dashboardType === "provider") {
        data = await getProviderAlertSummary();
      } else {
        data = await getAdminAlertSummary();
      }

      setAlertData(data);

      // Fetch expiring items for both admin and client (not provider)
      if (dashboardType !== "provider") {
        try {
          const expData = await getExpiringItems();
          const now = new Date();
          let count = 0;
          let critical = 0;
          const countItem = (expiresAt: string) => {
            count++;
            const days = Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            if (days <= 3) critical++;
          };
          if (Array.isArray(expData?.quotes)) expData.quotes.forEach((q: any) => countItem(q.expiresAt));
          if (Array.isArray(expData?.rfqs)) expData.rfqs.forEach((r: any) => countItem(r.expiresAt));
          setExpiringCount(count);
          setExpiringCriticalCount(critical);
        } catch (e) {
          // Silently fail
        }
      }

      setLoading(false);
    };
    fetchAlerts();
  }, [dashboardType]);

  // Auto-open modal once per session when critical items exist
  useEffect(() => {
    if (loading) return;
    const hasCritical = isAdmin
      ? ((alertData as AdminAlertSummary)?.criticalCount > 0 || expiringCriticalCount > 0)
      : expiringCriticalCount > 0;
    if (!hasCritical) return;
    try {
      const shown = sessionStorage.getItem("actionItemsModalAutoShown");
      // One pop-up per login: skip if an announcement or the Discount
      // Reminder already took this session's slot (helpers/loginModal).
      if (!shown && claimLoginModal("action-items")) {
        setIsModalOpen(true);
        sessionStorage.setItem("actionItemsModalAutoShown", "1");
      }
    } catch (e) {
      // sessionStorage may not be available
    }
  }, [loading, alertData, expiringCriticalCount, isAdmin]);

  let totalCount = 0;
  let summaryText = "";

  if (isAdmin) {
    const adminData = alertData as AdminAlertSummary;
    const opsCount = adminData?.operationsCount || 0;
    const sigsCount = adminData?.signalsCount || 0;

    if (opsCount > 0 || sigsCount > 0 || expiringCount > 0) {
      // New admin format: operations + signals + expiring
      totalCount = opsCount + sigsCount + expiringCount;
      const parts: string[] = [];
      if (opsCount > 0) parts.push(opsCount + (opsCount === 1 ? " operation" : " operations"));
      if (expiringCount > 0) parts.push(expiringCount + " expiring");
      if (sigsCount > 0) parts.push(sigsCount + (sigsCount === 1 ? " signal" : " signals"));
      summaryText = parts.join(", ");
    } else {
      // Fallback: old alert-summary categories (stale quotes, overdue invoices, etc.)
      totalCount = adminData?.categories?.reduce((sum, c) => sum + c.count, 0) || 0;
      if (adminData?.categories) {
        summaryText = adminData.categories.map(c => c.count + " " + c.label).join(", ");
      }
    }
  } else {
    const baseCount = alertData?.totalCount || 0;
    totalCount = baseCount + expiringCount;
    const summaryParts: string[] = [];
    if (alertData?.categories) {
      for (const cat of alertData.categories) {
        summaryParts.push(cat.count + " " + cat.label);
      }
    }
    if (expiringCount > 0) {
      const label = expiringCount === 1 ? "expiring quote/RFQ" : "expiring quotes & RFQs";
      summaryParts.push(expiringCount + " " + label);
    }
    summaryText = summaryParts.join(", ");
  }

  if (loading || totalCount === 0) {
    return null;
  }

  const handleViewAll = () => {
    if (dashboardType === "provider") {
      const element = document.getElementById("pos-awaiting-response");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <Flex
        sx={{
          backgroundColor: "#FDF3F3",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "24px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Flex sx={{ alignItems: "center", gap: "12px" }}>
          <Flex
            sx={{
              backgroundColor: themevals.colors.danger,
              borderRadius: "6px",
              padding: "8px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningIcon width="20px" height="20px" fill="#FDF3F3" />
          </Flex>
          <Box>
            <Box
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                color: themevals.colors.danger,
                lineHeight: 1.4,
              }}
            >
              Action Required: {totalCount} {totalCount === 1 ? "item needs" : "items need"} your attention
            </Box>
            {summaryText && (
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 400,
                  color: themevals.colors.danger,
                  lineHeight: 1.4,
                }}
              >
                {summaryText}
              </Box>
            )}
          </Box>
        </Flex>
        <BasicButton scheme="danger" onClick={handleViewAll}>
          View All
        </BasicButton>
      </Flex>

      {dashboardType !== "provider" && isModalOpen && (
        <ActionItemsModal onClose={() => setIsModalOpen(false)} dashboardType={dashboardType} />
      )}
    </>
  );
};

export default ActionRequiredBanner;
